import { IMenuDomain } from '../index';
import IconMi from 'components/common/icon/IconMi';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconHandler from 'components/common/icon/IconHandler';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { useSelector } from 'react-redux';
import useBundleTranslation from 'i18n';

interface IProps {
    domain: IMenuDomain;
    onSelectPrompt: (domain: string, prompt: string) => void;
    needsAuth: boolean;
    closeMenu: () => void;
    onAuthClick?: () => void;
    onAuthPromptClick?: (prompt: string) => void;
}

export default function ExtraMenu({
    domain,
    onSelectPrompt,
    needsAuth,
    closeMenu,
    onAuthClick,
    onAuthPromptClick,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const [hover, setHover] = useState(false);
    const hideTimerRef = useRef<number | null>(null);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    // Portal position computed once on mount from trigger rect (viewport coords)
    const [portalPos, setPortalPos] = useState<{ top: number; left?: number; right?: number } | null>(null);

    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.footerDomainSelector, 'extraMenu');
    }, [customStyles]);

    const handleEnter = () => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
        // Compute portal position at hover time — Popper is fully positioned by then
        const triggerEl = triggerRef.current;
        if (triggerEl) {
            const rect = triggerEl.getBoundingClientRect();
            const top = rect.top + rect.height / 2;
            if (rect.right + 312 <= window.innerWidth) {
                setPortalPos({ top, left: rect.right - 8 });
            } else {
                setPortalPos({ top, right: window.innerWidth - rect.left + 8 });
            }
        }
        setHover(true);
    };

    const handleLeave = () => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = window.setTimeout(() => {
            setHover(false);
            hideTimerRef.current = null;
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, []);

    const iconProps = {
        type: domain.icon_ind,
        value: domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
    };

    const showExpandMenu = domain.description || (domain.suggestedPrompts && domain.suggestedPrompts.length > 0);

    const handleAuthClick = (event: React.MouseEvent) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onAuthClick?.();
        closeMenu();
    };

    const handleAuthPromptClick = (event: React.MouseEvent, prompt: string) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onAuthPromptClick?.(prompt);
        closeMenu();
    };

    const onSelectExample = (event: React.MouseEvent, prompt: string) => {
        (event as any).__miCloseDropdown = true;
        event.preventDefault();
        event.stopPropagation();
        onSelectPrompt(domain.name, prompt);
        closeMenu();
    };

    const portalSx = {
        ...styles.extraMenu,
        position: 'fixed' as const,
        top: portalPos ? `${portalPos.top}px` : 0,
        left: portalPos?.left !== undefined ? `${portalPos.left}px` : 'auto',
        right: portalPos?.right !== undefined ? `${portalPos.right}px` : 'auto',
        transform: 'translateY(-50%)',
        zIndex: 1600,
    };

    if (needsAuth) {
        if (!showExpandMenu) {
            return (
                <Box>
                    <Tooltip title={t('footer.auth_required')} arrow placement="top">
                        <Box
                            ref={triggerRef}
                            width={'21px'}
                            height={'21px'}
                            display="flex"
                            justifyContent={'center'}
                            alignItems={'center'}
                            sx={{ backgroundColor: (theme) => theme.palette.warning.main, borderRadius: '3px' }}
                        >
                            <IconMi icon={'key'} fontSize="16" />
                        </Box>
                    </Tooltip>
                </Box>
            );
        }

        return (
            <>
                <Box onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
                    <Box
                        ref={triggerRef}
                        width={'21px'}
                        height={'21px'}
                        display="flex"
                        justifyContent={'center'}
                        alignItems={'center'}
                        sx={{ backgroundColor: (theme) => theme.palette.warning.main, borderRadius: '3px' }}
                    >
                        <IconMi icon={'key'} fontSize="16" />
                    </Box>
                </Box>
                {hover &&
                    portalPos &&
                    createPortal(
                        <Stack onMouseEnter={handleEnter} onMouseLeave={handleLeave} sx={portalSx} spacing={1}>
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                spacing={1}
                                justifyContent={'flex-start'}
                                onClick={handleAuthClick}
                                sx={{
                                    cursor: 'pointer',
                                    p: 1,
                                    mb: 1,
                                    backgroundColor: 'action.hover',
                                    borderRadius: 1,
                                    '&:hover': {
                                        backgroundColor: 'action.selected',
                                    },
                                }}
                            >
                                <IconMi icon={'key'} fontSize="16" />
                                <Typography fontWeight={600}>{t('footer.auth_required')}</Typography>
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'}>
                                <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                                <Typography fontWeight={600}>{domain.title}</Typography>
                            </Stack>
                            <Box>{domain.description && <Typography>{domain.description}</Typography>}</Box>
                            {domain.suggestedPrompts && domain.suggestedPrompts.length > 0 && (
                                <Stack sx={styles.examplesList} spacing={0.5}>
                                    {domain.suggestedPrompts.map((prompt, index) => (
                                        <Stack
                                            key={index}
                                            direction={'row'}
                                            sx={styles.examplesItem}
                                            onClick={(e) => handleAuthPromptClick(e, prompt)}
                                        >
                                            <Box flexGrow={1}>{prompt}</Box>
                                            <Box sx={styles.examplesItemIcon}>
                                                <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>,
                        document.body,
                    )}
            </>
        );
    }

    return showExpandMenu ? (
        <>
            <Box onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
                <Box
                    ref={triggerRef}
                    width={'21px'}
                    height={'21px'}
                    display="flex"
                    justifyContent={'center'}
                    alignItems={'center'}
                >
                    <IconMi
                        icon={domain.reportDataState === 'no_access' ? 'alert' : 'info-circle'}
                        fontSize="16"
                        sx={domain.reportDataState === 'no_access' ? { color: 'warning.main' } : undefined}
                    />
                </Box>
            </Box>
            {hover &&
                portalPos &&
                createPortal(
                    <Stack onMouseEnter={handleEnter} onMouseLeave={handleLeave} sx={portalSx} spacing={1}>
                        <Stack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'}>
                            <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />
                            <Typography fontWeight={600}>{domain.title}</Typography>
                        </Stack>
                        <Box>{domain.description && <Typography>{domain.description}</Typography>}</Box>
                        {domain.suggestedPrompts && domain.suggestedPrompts.length > 0 && (
                            <Stack sx={styles.examplesList} spacing={0.5}>
                                {domain.suggestedPrompts.map((prompt, index) => (
                                    <Stack
                                        key={index}
                                        direction={'row'}
                                        sx={styles.examplesItem}
                                        onClick={(e) => onSelectExample(e, prompt)}
                                    >
                                        <Box flexGrow={1}>{prompt}</Box>
                                        <Box sx={styles.examplesItemIcon}>
                                            <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>,
                    document.body,
                )}
        </>
    ) : null;
}
